/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { ensureCurrentUser } from '../../../util/data';

import {
  AvatarLarge,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
  NewListingLink,
  ExternalLink
} from '../../../components';

import css from './TopbarMobileMenu.module.css';

const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    currentUserHasListings,
    currentUser,
    notificationCount,
    onLogout,
  } = props;

  const user = ensureCurrentUser(currentUser);
  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const displayName = user.attributes.profile.firstName;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  if (!isAuthenticated) {
    const signup = (
      <NamedLink name="SignupPage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage id="TopbarMobileMenu.signupOrLogin" values={{ signup, login }} />
      </span>
    );
    return (
      <div className={css.root}>
        <div className={css.content}>
          <div className={css.authenticationGreeting}>
            <FormattedMessage
              id="TopbarMobileMenu.unauthorizedGreeting"
              values={{ lineBreak: <br />, signupOrLogin }}
            />
          </div>

          <NamedLink
            name="CMSPage"
            params={{ pageId: 'how-it-works' }}
            className={classNames(css.inbox, currentPageClass('CMSPage'))}
          >
            <span className={css.login}>
              <FormattedMessage id="TopbarMobileMenu.howItWorksLink" />
            </span>
          </NamedLink>

          <span className={classNames(css.navigationLink, currentPageClass('NewListingPage'))}>
            <NewListingLink textId="TopbarMobileMenu.newListingLink" />
          </span>

          <ExternalLink
            href="https://www.upisle.com/boating-tips/upisle-blog/"
            className={css.navigationLink}
          >
            <FormattedMessage id="TopbarMobileMenu.blogLink" />
          </ExternalLink>

          <NamedLink name="CMSPage" params={{ pageId: 'faq' }} className={css.navigationLink}>
            <FormattedMessage id="TopbarDesktop.faq" />
          </NamedLink>

          <NamedLink
            name="ContactUsPage"
            className={classNames(css.navigationLink, currentPageClass('ContactUsPage'))}
          >
            <FormattedMessage id="TopbarMobileMenu.contactUsLink" />
          </NamedLink>

          <a href="tel:7862323657" className={css.navigationLink}>
            786-232-3657
          </a>

        </div>
        <div className={css.footer}>
          <NamedLink
            name="SearchPage"
            className={css.createNewListingLink}
          >
            <FormattedMessage id="TopbarMobileMenu.searchRentals" />
          </NamedLink>
        </div>
      </div>
    );
  }

  return (
    <div className={css.root}>
      <AvatarLarge className={css.avatar} user={currentUser} />
      <div className={css.content}>
        <span className={css.greeting}>
          <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
        </span>
        <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton>

        <NamedLink
          name="CMSPage"
          params={{ pageId: 'how-it-works' }}
          className={classNames(css.inbox, currentPageClass('CMSPage'))}
        >
          <span className={css.login}>
            <FormattedMessage id="TopbarMobileMenu.howItWorksLink" />
          </span>
        </NamedLink>

        <NamedLink
          className={classNames(css.navigationLink, currentPageClass('InboxPage'))}
          name="InboxPage"
          params={{ tab: currentUserHasListings ? 'sales' : 'orders' }}
        >
          <FormattedMessage id="TopbarMobileMenu.inboxLink" />
          {notificationCountBadge}
        </NamedLink>

        <span className={classNames(css.navigationLink, currentPageClass('NewListingPage'))}>
          <NewListingLink textId="TopbarMobileMenu.newListingLink" />
        </span>

        <NamedLink
          className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}
          name="ManageListingsPage"
        >
          <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
        </NamedLink>

        <NamedLink
          className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}
          name="ProfileSettingsPage"
        >
          <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
        </NamedLink>

        <NamedLink
          className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}
          name="AccountSettingsPage"
        >
          <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
        </NamedLink>

        <ExternalLink
          href="https://www.upisle.com/boating-tips/upisle-blog/"
          className={css.navigationLink}
        >
          <FormattedMessage id="TopbarMobileMenu.blogLink" />
        </ExternalLink>

        <NamedLink name="CMSPage" params={{ pageId: 'faq' }} className={css.navigationLink}>
          <FormattedMessage id="TopbarDesktop.faq" />
        </NamedLink>

        <NamedLink
          name="CMSPage"
          params={{ pageId: 'refer-a-contact' }}
          className={css.navigationLink}
        >
          <FormattedMessage id="TopbarDesktop.referAContact" />
        </NamedLink>

        <NamedLink
          name="ContactUsPage"
          className={classNames(css.navigationLink, currentPageClass('ContactUsPage'))}
        >
          <FormattedMessage id="TopbarMobileMenu.contactUsLink" />
        </NamedLink>

        <a href="tel:7862323657" className={css.navigationLink}>
          786-232-3657
        </a>

        <div className={css.spacer} />
      </div>
      <div className={css.footer}>
        <NamedLink
          name="SearchPage"
          className={css.createNewListingLink}
        >
          <FormattedMessage id="TopbarMobileMenu.searchRentals" />
        </NamedLink>
      </div>
    </div>
  );
};

TopbarMobileMenu.defaultProps = { currentUser: null, notificationCount: 0, currentPage: null };

const { bool, func, number, string } = PropTypes;

TopbarMobileMenu.propTypes = {
  isAuthenticated: bool.isRequired,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  notificationCount: number,
  onLogout: func.isRequired,
};

export default TopbarMobileMenu;
